import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ZoomSyncContext } from 'business/models/CustomModels';
import TransformPoints from 'components/plateviewer/well/TransformPoints';
import WellCanvasInfoHeader from 'components/plateviewer/well/wellCanvasInfoHeader';
import RMWCanvas from 'components/plateviewer/RMWCanvas';
import DrawingManager from './drawingManager/drawingManager';
import {
  ImageSizeDetails,
  DropDetails,
  scaleBarHeight,
  scaleBarWidth,
  scaleBarGroupWidth,
  getDefaultScaleBarPosition,
  ScaleBarPosition,
} from './constants';
import WellImageManager from './wellImage/wellImageManager';
import { useDrawingCreationHandlers } from './drawingManager/DrawingsCreator';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  useIsWellImagingEnabled,
  useSelectedDrawingTool,
  useSelectedWellDrawing,
} from 'redux/hooks/well';
import {
  addNewDrawing,
  setSelectedWellDropDetailsIndex,
  updateDrawing,
} from 'redux/reducers/wellReducer';
import { OverlayTools } from './drawingManager/constants';
import { deleteDrawing, saveDrawingThunk } from 'redux/thunks/well';
import drawingSaveManager from 'utils/DrawingSaveManager';
import { changeDrawingIcon, getImageZoomVisibleArea, getMinimumZoomLevel } from './utils';
import Scores from 'components/plateviewer/plate/Scores';
import { useFetchWellImagingDetails, useImageZoomVisibleArea } from './hooks';
import { useWellDropsWithIngredients, useWellScores } from 'redux/hooks/searchnFilter';
import { callGenerateLayer } from 'redux/reducers/experimentReducer';
import { Rect } from 'react-konva';
import { useIsZoomLocked } from 'redux/hooks/common';
import { selectHideAutoScores, selectScoreGroups } from 'redux/selectors/StateSelectors';
import { filterScores } from 'components/searchAndFilter/utils';
import { isDropModeUrl } from 'utils/Global';
import { SizeDetails } from 'components/canvas/constants';
import { navigateWellByKey } from 'redux/reducers/searchnFilterReducer';
import { KeyboardKeys } from 'constants/types';
import RefreshScale, {
  getMicronsPerCanvasPixel,
} from 'components/plateviewer/well/ScaleBar';
import ScaleBarRenderer from 'components/plateviewer/well/ScaleBarRenderer';
import { DEFAULT_WELL_SCORE_SIZE } from '../../constants/scoreConstants';

interface Props {
  sizeDetails: SizeDetails;
  wellDetails: DropDetails;
  maxImageSizeDetails?: ImageSizeDetails;
  selectedWell?: DropDetails;
  isMultipleDropsRoute?: boolean;
  updateWellsImagesSizeDetails?: (val: ImageSizeDetails) => void;
  isTraversingVertical?: boolean;
  wellIndex: number;
  selectedWellIndex: number;
  setStageRef?: (ref, wellIndex: number, wellKey: string) => void;
  onZoomChange?: (
    stage: any,
    oldScale: number,
    scale: number,
    bestFit: boolean,
    wellKey: string,
    syncContext?: ZoomSyncContext,
  ) => void;
  setZoomInvocker: (functionDelegate: (zoom: number) => void) => void;
  selectedWellsLength?: number;
  wellKey?: string;
  isIndependentZoom?: boolean;
  setWellRef?: (wellKey: string, ref: any) => void;
  minZoomLevel;
  totalWells;
}

let isCreatingNewDrawing = false;
let currentWellIndex = -1;
let virtualMouse = null;
const Well = (props: Props) => {
  const {
    sizeDetails,
    wellDetails,
    maxImageSizeDetails = undefined,
    updateWellsImagesSizeDetails,
    wellIndex,
    setStageRef = undefined,
    onZoomChange = undefined,
    setZoomInvocker,
    selectedWellsLength,
    wellKey,
    isIndependentZoom,
    setWellRef,
    minZoomLevel = 1,
    totalWells,
  } = props;
  const { plateID, wellNum, dropNumber, cpID, imageBatchId, dropId } = wellDetails;
  const wellScores = useWellScores(dropId, imageBatchId);
  const wellDrops = useWellDropsWithIngredients();

  const wellLabel = useMemo(() => {
    const wellDrop = wellDrops.find((x) => x.wellNumber === wellNum);
    if (!wellDrop) {
      return '';
    }
    return `${wellDrop?.rowLetter}${wellDrop?.columnNumber}`;
  }, [wellDrops, wellNum]);

  const [isHoverOnWell, setIsHoverOnWell] = useState<boolean>(false);
  const [tpObject, setTpObject] = useState<TransformPoints>();
  const [pixelSize, setPixelSize] = useState(0);
  const [stage, setStage] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [draggedScaleBarPosition, setDraggedScaleBarPosition] =
    useState<ScaleBarPosition | null>(null);

  const drawingTool = useSelectedDrawingTool();
  const selectedDrawing = useSelectedWellDrawing();
  const dispatch = useRMDispatch();
  const isImagingEnabled = useIsWellImagingEnabled(
    wellDetails?.plateID,
    wellDetails?.wellNum,
    wellDetails?.dropNumber,
  );
  const scoreGroups = useRMSelector((state) => selectScoreGroups(state));
  const hideAutoScores = useRMSelector((state) => selectHideAutoScores(state));
  // eslint-disable-next-line
  const { status, ...wellImagingDetails } = useFetchWellImagingDetails(wellDetails);
  const isZoomLocked = useIsZoomLocked();
  const { regionName, cpName, inspectionName } = wellImagingDetails || {};
  const drawingCreateEvents = useDrawingCreationHandlers(
    drawingTool,
    wellDetails,
    regionName,
    tpObject,
  );
  const { onMouseDown, onMouseMove, onMouseUp } = drawingCreateEvents || {};
  const isDropMode = isDropModeUrl();
  const { setImageZoomVisibleArea } = useImageZoomVisibleArea();

  const stageRef = useCallback(
    (stage) => {
      setStage(stage);
      if (setStageRef) {
        setStageRef(stage, wellIndex, wellKey);
      }
    },
    [setStage, setStageRef, wellIndex],
  );
  const canvasRef = useRef(null);

  const onZoomChangeEvent = useCallback(
    (stage, scale, newScale, bestFit, syncContext?: ZoomSyncContext) => {
      setZoomScale(newScale);
      setVirtualMouseDetails(stage);
      if (onZoomChange) {
        onZoomChange(stage, zoomScale, newScale, bestFit, wellKey, syncContext);
      }
      setImageZoomVisibleArea(getImageZoomVisibleArea(stage, tpObject, newScale));
    },
    [setZoomScale, onZoomChange, setImageZoomVisibleArea, tpObject],
  );

  const onMouseEnter = useCallback(
    (e) => {
      if (drawingTool !== OverlayTools.POINTER || selectedDrawing) {
        changeDrawingIcon(e, 'crosshair');
      }
    },
    [drawingTool, selectedDrawing],
  );

  const onMouseLeave = useCallback((e) => {
    changeDrawingIcon(e, 'default');
  }, []);

  const onCanvasClick = () => {
    if (!isCreatingNewDrawing && drawingTool === OverlayTools.POINTER) {
      dispatch(setSelectedWellDropDetailsIndex(wellIndex));
    }
  };

  const handleOnMouseDown = (e) => {
    if (onMouseDown) {
      const drawingDetails = onMouseDown(e);
      isCreatingNewDrawing = true;
      if (drawingDetails) {
        const { drawing, type } = drawingDetails;
        dispatch(
          addNewDrawing({
            plateId: plateID,
            wellNumber: wellNum,
            dropNumber,
            regionName,
            drawingType: type,
            drawingUpdateDetails: drawing,
          }),
        );
      }
    }
  };

  const handleOnMoveMove = (e) => {
    currentWellIndex = wellIndex;
    const stage = e.target.getStage();
    if (!stage) {
      return;
    }
    if (!isDropMode) {
      setVirtualMouseDetails(stage);
    }
    if (drawingTool === OverlayTools.POINTER) {
      changeDrawingIcon(e, 'default');
    } else {
      changeDrawingIcon(e, 'crosshair');
    }
    if (onMouseMove && isCreatingNewDrawing) {
      const drawingDetails = onMouseMove(e);
      if (drawingDetails) {
        const { drawing, type, isDrawingFirstClick } = drawingDetails;
        if (isDrawingFirstClick) {
          dispatch(
            addNewDrawing({
              plateId: wellDetails.plateID,
              wellNumber: wellDetails.wellNum,
              dropNumber: wellDetails.dropNumber,
              regionName: regionName,
              drawingType: type,
              drawingUpdateDetails: drawing,
            }),
          );
        } else {
          dispatch(
            updateDrawing({
              plateId: plateID,
              wellNumber: wellNum,
              dropNumber,
              regionName: regionName,
              drawingType: type,
              id: drawing.id,
              drawingUpdateDetails: drawing,
            }),
          );
        }
      }
    }
  };

  const handleOnMouseUp = async (e) => {
    if (onMouseUp && isCreatingNewDrawing) {
      isCreatingNewDrawing = false;
      const drawingDetails = await onMouseUp(e);
      if (drawingDetails) {
        const { drawing, type } = drawingDetails;
        const savePromise = dispatch(
          saveDrawingThunk({
            plateId: plateID,
            wellNumber: wellNum,
            dropNumber,
            cpId: cpID,
            imageBatchId,
            regionName,
            drawingType: type,
            drawingUpdateDetails: drawing,
            tpObject,
          }),
        );
        drawingSaveManager.save(drawing.id, savePromise);
      }
    } else {
      isCreatingNewDrawing = false;
    }
  };

  const genCanvas = useCallback(() => dispatch(callGenerateLayer()), [dispatch]);
  const scoresToShow = useMemo(() => {
    if (!wellScores?.length) {
      return [];
    }
    const filteredScores = filterScores(scoreGroups, wellScores, hideAutoScores);

    return (
      <Scores
        id="well-scorestoshow"
        scoresOfWellDrops={filteredScores}
        width={DEFAULT_WELL_SCORE_SIZE}
        height={DEFAULT_WELL_SCORE_SIZE}
        x={0}
        y={0}
        scale={0}
        icon={null}
        wellView={true}
        sizeDetails={sizeDetails}
      />
    );
  }, [wellScores, scoreGroups, hideAutoScores]);

  const setVirtualMouseDetails = useCallback(
    (stageRef) => {
      const pos = stageRef.getPointerPosition();
      if (pos && stageRef) {
        virtualMouse = (
          <Rect
            x={(pos.x - stageRef.x()) / stageRef.scaleX()}
            y={(pos.y - stageRef.y()) / stageRef.scaleX()}
            width={8 / stageRef.scaleX()}
            height={8 / stageRef.scaleX()}
            stroke={'white'}
            fill={'black'}
            strokeWidth={1 / stageRef.scaleX()}
          />
        );
        genCanvas();
      }
    },
    [virtualMouse, genCanvas],
  );

  const onKeyPress = (e: KeyboardEvent) => {
    const { key } = e;
    if (key === KeyboardKeys.Delete && selectedDrawing) {
      dispatch(deleteDrawing(selectedDrawing));
    } else {
      dispatch(navigateWellByKey(key));
    }
  };

  const setCanvasRef = (instance) => {
    setWellRef(wellKey, instance);
    canvasRef.current = instance;
  };

  useEffect(() => {
    const _minZoomLevel = getMinimumZoomLevel(
      isIndependentZoom,
      totalWells,
      minZoomLevel,
    );
    if (_minZoomLevel > 1 && canvasRef) {
      canvasRef?.current?.ZoomHandler(_minZoomLevel);
    }
  }, [wellDetails, , canvasRef]);

  useEffect(() => {
    const scale = stage ? stage.scaleX() : 1;
    setZoomScale(scale);
  }, [stage?.attrs.scaleX]);

  const scaleDetails = useMemo(() => {
    return RefreshScale(
      getMicronsPerCanvasPixel(tpObject, pixelSize),
      zoomScale,
      scaleBarWidth,
    );
  }, [pixelSize, zoomScale, tpObject]);
  const scaleBarPosition =
    draggedScaleBarPosition ?? getDefaultScaleBarPosition(sizeDetails.canvasHeight);
  return (
    <div
      id="well-main-container"
      style={{ display: 'flex', flexDirection: 'column' }}
      onMouseEnter={() => setIsHoverOnWell(true)}
      onMouseLeave={(e) => {
        setIsHoverOnWell(false);
        handleOnMouseUp(e);
      }}
    >
      <WellCanvasInfoHeader
        wellKey={wellKey}
        canvasWidth={sizeDetails.canvasWidth}
        wellScores={scoresToShow}
        wellNumber={wellLabel ? ((wellLabel + '-' + dropNumber) as string) : ''}
        wellInfo={`Drop ${dropNumber}${
          Object.keys(wellImagingDetails)?.length
            ? ` - ${regionName} - ${cpName} - ${inspectionName}`
            : ''
        }${''}`}
        isImagingDisabled={!isImagingEnabled}
        selectedWellsLength={selectedWellsLength}
        isIndependentZoom={isIndependentZoom}
      />
      <RMWCanvas
        ref={setCanvasRef}
        key={wellKey}
        width={sizeDetails.canvasWidth}
        height={sizeDetails.canvasHeight}
        canvasWidth={sizeDetails.canvasWidth}
        canvasHeight={sizeDetails.canvasHeight}
        stageRef={stageRef}
        draggable={true}
        cols={1}
        rows={1}
        stage={stage}
        onZoomChange={onZoomChangeEvent}
        showScoresToolTip={isHoverOnWell}
        onMouseDown={handleOnMouseDown}
        onMouseMove={handleOnMoveMove}
        onMouseUp={handleOnMouseUp}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        isDrawingActive={isCreatingNewDrawing}
        onClick={onCanvasClick}
        setZoomInvocker={setZoomInvocker}
        isZoomLocked={isZoomLocked && isDropMode}
        onKeyPress={onKeyPress}
        minZoomLevel={getMinimumZoomLevel(isIndependentZoom, totalWells, minZoomLevel)}
        fixedContent={
          <ScaleBarRenderer
            x={scaleBarPosition.x}
            y={scaleBarPosition.y}
            width={scaleBarGroupWidth}
            height={scaleBarHeight}
            scaleBarWidth={scaleDetails.Width}
            scaleBarHeight={6}
            fontSize={15}
            text={scaleDetails.Text}
            scale={1}
            draggable
            bounds={{ width: sizeDetails.canvasWidth, height: sizeDetails.canvasHeight }}
            onPositionChange={(x, y) => setDraggedScaleBarPosition({ x, y })}
          />
        }
      >
        <WellImageManager
          wellDetails={wellDetails}
          sizeDetails={sizeDetails}
          tpObject={tpObject}
          setTpObject={setTpObject}
          setPixelSize={setPixelSize}
          maxImageSizeDetails={maxImageSizeDetails}
          updateWellsImagesSizeDetails={updateWellsImagesSizeDetails}
          wellIndex={wellIndex}
        />
        <DrawingManager
          canvasWidth={sizeDetails.canvasWidth}
          canvasHeight={sizeDetails.canvasHeight}
          wellDetails={wellDetails}
          regionName={regionName}
          tpObject={tpObject}
          zoomScale={zoomScale}
        />
        <VirtualMouse wellIndex={wellIndex} />
      </RMWCanvas>
    </div>
  );
};

interface VMProps {
  wellIndex: number;
}

const VirtualMouse = (props: VMProps) => {
  const { wellIndex } = props;
  return wellIndex !== currentWellIndex ? virtualMouse : null;
};

export default memo(Well);
